import { useEffect, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Input } from '@/design-system';
import { formatCnpjCpf, formatPhone, unmask, unmaskDocument } from '@/design-system/utils/masks';
import type { Affiliate, UpdateAffiliateData } from '../api/affiliates.service';

type EditForm = {
  name: string;
  email: string;
  document: string;
  phone: string;
  commissionPercent: string;
  isActive: boolean;
};

interface AffiliateEditDialogProps {
  isOpen: boolean;
  affiliate: Affiliate | null;
  onClose: () => void;
  updateAffiliate: UseMutationResult<
    unknown,
    unknown,
    { id: string; body: UpdateAffiliateData },
    unknown
  >;
}

export function AffiliateEditDialog({ isOpen, affiliate, onClose, updateAffiliate }: AffiliateEditDialogProps) {
  const [formData, setFormData] = useState<EditForm>({
    name: '',
    email: '',
    document: '',
    phone: '',
    commissionPercent: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!affiliate || !isOpen) return;
    setFormData({
      name: affiliate.name,
      email: affiliate.email,
      document: affiliate.document ? formatCnpjCpf(affiliate.document) : '',
      phone: affiliate.phone ? formatPhone(affiliate.phone) : '',
      commissionPercent: affiliate.commissionPercent || '',
      isActive: affiliate.isActive,
    });
    setFormErrors({});
  }, [affiliate, isOpen]);

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (!formData.email.trim()) errors.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'E-mail inválido';
    const commission = parseFloat(formData.commissionPercent);
    if (formData.commissionPercent && (Number.isNaN(commission) || commission < 0 || commission > 100)) {
      errors.commissionPercent = 'Comissão deve ser entre 0 e 100';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  const handleSave = async () => {
    if (!affiliate || !validateEditForm()) return;
    try {
      await updateAffiliate.mutateAsync({
        id: affiliate.id,
        body: {
          name: formData.name,
          email: formData.email,
          document: unmaskDocument(formData.document) || undefined,
          phone: unmask(formData.phone) || undefined,
          commissionPercent: formData.commissionPercent || undefined,
          isActive: formData.isActive,
        },
      });
      handleClose();
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Editar afiliado" size="md" footer={(
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={updateAffiliate.isPending}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={() => void handleSave()} isLoading={updateAffiliate.isPending}>
          Salvar
        </Button>
      </div>
    )}>
      <div className="space-y-4">
        <Input
          label="Nome"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={formErrors.name}
        />
        <Input
          label="E-mail"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={formErrors.email}
        />
        <Input
          label="CPF/CNPJ (opcional)"
          value={formData.document}
          onChange={(e) => setFormData((prev) => ({ ...prev, document: formatCnpjCpf(e.target.value) }))}
          placeholder="000.000.000-00 ou 00.000.000/0001-00"
        />
        <Input
          label="Telefone (opcional)"
          value={formData.phone}
          onChange={(e) => setFormData((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
          placeholder="(11) 99999-9999"
        />
        <Input
          label="Comissão % (opcional)"
          type="number"
          min={0}
          max={100}
          value={formData.commissionPercent}
          onChange={(e) => {
            const v = e.target.value;
            const num = parseInt(v, 10);
            setFormData((prev) => ({
              ...prev,
              commissionPercent: v === '' ? '' : String(Number.isNaN(num) ? 0 : Math.min(100, Math.max(0, num))),
            }));
          }}
          error={formErrors.commissionPercent}
          placeholder="0 a 100"
        />
        <Checkbox
          label="Afiliado ativo"
          checked={formData.isActive}
          onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
        />
      </div>
    </Dialog>
  );
}
